import WorkflowQualityNote from './WorkflowQualityNote';
import { qualityWaitOf } from './workflowQualityWaitPolicy';
import WorkflowDirectingTailLine from './WorkflowDirectingTailLine';
import { filterBarOf } from './workflowFilterPolicy';
// 261010 UI1a — nội dung MỘT bước của khung 8 bước (cấp plan): đầu bước (tên · mô tả · nút Chạy/Duyệt) + danh sách theo bước.
// Tài sản = P8e nguyên (`WorkflowAssetsTabs`, một lần cho cả plan). Bước khác = danh sách video theo cảnh; sửa ở ngăn Chi tiết (`?event=&step=`).
// U32b (29/10): ô 7 «Prompt & Video» (khoá VIDEO) = màn 3 cột `WorkflowPromptVideoStep` (danh sách video · prompt · player + bản clip + thông số + Gen) thay cho danh sách dòng; không còn bộ lọc / mở-thu ở ô này. Nút cần lõi chưa có hiện nhưng TẮT kèm chú thích.
// 261010 UI1c — plan NHIỀU video: mỗi video MỘT dòng tóm tắt (bấm mở rộng tại chỗ); mỗi cảnh thu/mở; bộ lọc mặc định "Cần xử lý" (lỗi + thiếu + cũ) khi có việc. Logic thuần ở workflowManyUtils.
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Spin, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { EventPlan, FilmEvent, PositionMapsResponse, StoryboardListItem } from '../../types/film';
import type { WorkflowStatus, WorkflowStepKey } from '../../types/filmWorkflow';
import {
  BOARD_STEPS, STATE_META, BoardFilter, filterEvents, groupByScene, stateFilterCounts, stateOfEvent,
} from './workflowBoardUtils';
import { attentionCountsOf, defaultOpenScenesOf, resolveStateFilter, rowSummaryOf, sceneLineOf } from './workflowManyUtils';
import { planStepAlertsOf } from './workflowBellPolicy';
import { stepFlagRowsOf, stepBadgeOf, scriptRerunCallOf, type PrimaryAction, type PrimaryCall, type SecondaryAction } from './workflowPrimaryPolicy';
import { stepHeadActionsOf, stepHintOf, headActionProminentOf, qualityWaitRedundantOf } from './workflowStepHeadPolicy';
import WorkflowSceneBlock from './WorkflowSceneBlock';
import WorkflowSceneTools from './WorkflowSceneTools';
import WorkflowNeedsSplitBanner from './WorkflowNeedsSplitBanner';
import { LookMismatchBar } from './WorkflowWarnBlocks';
import { needsSplitBannerOf, needsSplitRemainingOf } from './workflowStructOps';
import WorkflowVideoRow from './WorkflowVideoRow';
import WorkflowStepContent from './WorkflowStepContent';
import WorkflowAssetsTabs from './WorkflowAssetsTabs';
import WorkflowAssetFlags from './WorkflowAssetFlags';
import WorkflowAssetsFailureLine from './WorkflowAssetsFailureLine';
import { ASSETS_STYLE_ALERT_CODE } from './assetsFailureLine';
import WorkflowPromptVideoStep from './WorkflowPromptVideoStep';
import { promptVideoSummaryOf } from './promptVideoUtils';
import StatusIcon from './StatusIcon';
import WfIcon from './WfIcon';
import WfIconButton from './WfIconButton';
import { headActionIconOf } from './workflowStatusIcons';

interface Props {
  status: WorkflowStatus;
  planId: string;
  storyId: string;
  step: WorkflowStepKey;
  stateFilter: BoardFilter['state'];
  onStateFilter: (s: BoardFilter['state']) => void;
  detail: EventPlan | null;
  detailLoading: boolean;
  detailError: string | null;
  onLoadDetail: () => void;
  storyboards: StoryboardListItem[] | null;
  storyboardsError: string | null;
  maps: PositionMapsResponse | null;
  mapsError: string | null;
  eventsById: Map<string, { ev: FilmEvent; sceneId: string }>;
  headerActions: SecondaryAction[];
  /** Nút chính ở hàng trên cùng — đầu mục bước không lặp lại hành động của nó (workflowStepHeadPolicy). */
  primary?: PrimaryAction;
  /** Không còn dùng ở bước Tài sản (dòng lỗi gọn không liệt kê mục); giữ prop để không đổi hợp đồng với WorkflowBoard. */
  assetNames?: Record<string, string>;
  sending: boolean;
  onRun: (call: PrimaryCall | null) => void;
  onOpenEvent: (id: string, step?: WorkflowStepKey | null) => void;
  /** Chuyển sang bước khác của khung 8 bước (vd. «Xem ở Tài sản» ở bước Đạo diễn). */
  onGoStep?: (step: WorkflowStepKey) => void;
  /** U32b — video đang chọn ở ô gộp (`?event=`) + chọn video khác. */
  selectedEventId?: string | null;
  onSelectEvent?: (eventId: string) => void;
  onActionDone: () => void;
  busyClipId: string | null;
  onSelectClip: (eventId: string, clipId: string) => void;
}

const Loading: React.FC<{ error: string | null; onRetry: () => void }> = ({ error, onRetry }) => (error
  ? <Alert type="warning" showIcon message={error} action={<Button size="small" onClick={onRetry}>Tải lại</Button>} />
  : <div style={{ padding: 14 }}><Spin size="small" /> <span className="muted">Đang tải chi tiết plan…</span></div>);

const WorkflowStepBoard: React.FC<Props> = ({
  status, planId, storyId, step, stateFilter, onStateFilter, detail, detailLoading, detailError, onLoadDetail, storyboards, storyboardsError, maps, mapsError,
  eventsById, headerActions, primary, sending, onRun, onOpenEvent, onGoStep, selectedEventId, onSelectEvent, onActionDone, busyClipId, onSelectClip,
}) => {
  const meta = BOARD_STEPS.find((s) => s.key === step)!;
  const mergedStep = step === 'VIDEO'; // U32b: ô 7 «Prompt & Video»
  const planSteps = status.steps;
  const sorted = useMemo(() => [...status.events].sort((a, b) => a.sceneOrder - b.sceneOrder || a.eventOrder - b.eventOrder), [status.events]);
  const badge = stepBadgeOf(status, step);
  const alerts = [...planStepAlertsOf(status.alerts, step), ...(mergedStep ? planStepAlertsOf(status.alerts, 'PROMPT') : [])].filter((a) => !(step === 'ASSETS' && a.code === ASSETS_STYLE_ALERT_CODE)); // hồ sơ phong cách: thẻ ở cuối bước đã nói · ô gộp: cảnh báo của cả Prompt lẫn Video
  const isEventStep = meta.scope === 'EVENT';
  // 261010 UI1c — bộ lọc mặc định "Cần xử lý" (lỗi + thiếu + cũ) khi có việc; không có việc ⇒ "Tất cả".
  const attn = useMemo(() => attentionCountsOf(sorted, step, planSteps), [sorted, step, planSteps]);
  const counts = stateFilterCounts(sorted, planSteps, step);
  // UI-directing: bộ lọc nào số về 0 thì ẩn; đang chọn mà hết thì rơi về «Tất cả»; «Cần xử lý» chỉ hiện khi > 0 (workflowFilterPolicy).
  const bar = filterBarOf(counts, attn.total, resolveStateFilter(stateFilter, attn.total));
  const eff: BoardFilter['state'] = isEventStep ? bar.effective : 'ALL';
  const filtered = isEventStep ? filterEvents(sorted, planSteps, { step, state: eff }) : sorted;
  const groupsAll = useMemo(() => groupByScene(sorted), [sorted]);
  const showAllScenes = !isEventStep || eff === 'ATTN' || eff === 'ALL'; // cảnh không có video khớp vẫn hiện một dòng (thu) để biết còn cảnh nào
  const groups = showAllScenes ? groupsAll : groupByScene(filtered);
  const attnIds = useMemo(() => new Set(attn.ids), [attn]);
  const defaultOpen = eff === 'ATTN' ? defaultOpenScenesOf(groupsAll, step, planSteps) : {};
  const [sceneOpen, setSceneOpen] = useState<Record<string, boolean>>({});
  const [rowOpen, setRowOpen] = useState<Record<string, boolean>>({});
  const [expandAll, setExpandAll] = useState<boolean | null>(null);
  useEffect(() => { setSceneOpen({}); setRowOpen({}); setExpandAll(null); }, [step, eff]);
  const isSceneOpen = (id: string): boolean => sceneOpen[id] ?? defaultOpen[id] ?? true;
  const alertCount = useMemo(() => {
    const m = new Map<string, number>();
    for (const al of status.alerts) if (al.subjectId) m.set(`${al.subjectId}|${al.stepKey}`, (m.get(`${al.subjectId}|${al.stepKey}`) ?? 0) + 1);
    return m;
  }, [status.alerts]);
  const isListStep = step !== 'ASSETS' && !mergedStep; // ô gộp có cột trái thay cho danh sách ⇒ không có thanh lọc / mở-thu
  const sceneInfo = new Map((status.scenes ?? []).map((s) => [s.sceneId, s]));
  const evOfId = (id: string): FilmEvent | null => eventsById.get(id)?.ev ?? null;
  const mergedLine = mergedStep ? promptVideoSummaryOf(sorted, evOfId) : '';
  const qWait = qualityWaitOf(status, step);
  const hintRaw = stepHintOf(step, headerActions, primary);
  const disabledWhy = qWait.waiting && hintRaw === qWait.text ? null : hintRaw; // dòng chờ của AI đã nói đúng câu đó ở ngay dưới đầu mục
  const shownActions = stepHeadActionsOf(headerActions, primary);
  // Lượt chạy Tài sản đổi trạng thái (bắt đầu / xong) ⇒ dựng lại các tab: chúng giữ dữ liệu riêng (dải thiếu, Bank, bối cảnh) không tự nạp lại. Đánh đổi: tab đang chọn về mặc định ở hai mốc đó.
  // P9D D5 — NEEDS_SPLIT còn sau chặng 1: nguồn = `generation.block` của chi tiết video + cờ bền của workflow-status.
  const splitRem = useMemo(() => needsSplitRemainingOf(sorted.map((e) => ({ eventOrder: e.eventOrder, stepFlags: e.stepFlags, generation: eventsById.get(e.eventId)?.ev.generation ?? null }))), [sorted, eventsById]);
  const scriptState = planSteps.find((x) => x.key === 'SCRIPT')?.state ?? 'NOT_READY';
  const rerunCall = step === 'SCRIPT' ? scriptRerunCallOf(status, { sending }) : null;
  const splitBanner = step === 'SCRIPT' ? needsSplitBannerOf(scriptState, splitRem, rerunCall?.type === 'RUN_STAGE1' && !!rerunCall.approved) : null;
  const refNames = useMemo(() => (detail?.refBank?.characters ?? []).map((c) => c.name), [detail]);
  const assetsKey = `${status.stepRuns?.ASSETS?.runId ?? 'none'}:${status.stepRuns?.ASSETS?.canStop ? 'run' : 'idle'}`;

  const body = (): React.ReactNode => {

    if (step === 'ASSETS') {
      const firstEv = sorted[0] ? eventsById.get(sorted[0].eventId)?.ev ?? null : null;
      // UI-assets: thẻ tài sản đứng đầu; lỗi chỉ là MỘT dòng gọn (có «Chạy lại») ngay dưới các tab — không còn hộp đỏ liệt kê khoá.
      const failLine = <WorkflowAssetsFailureLine status={status} actions={headerActions} sending={sending} onRun={onRun} />;
      return detail
        ? <WorkflowAssetsTabs key={assetsKey} planId={planId} storyId={storyId} ev={firstEv} detail={detail} jobs={status.jobs} onActionDone={onActionDone} notice={failLine} />
        : <><Loading error={detailError} onRetry={onLoadDetail} />{failLine}</>;
    }
    if (mergedStep) {
      return (
        <WorkflowPromptVideoStep status={status} planId={planId} storyId={storyId} events={sorted} detail={detail} detailLoading={detailLoading} detailError={detailError} onLoadDetail={onLoadDetail}
          eventsById={eventsById} requestedId={selectedEventId ?? null} onSelectVideo={onSelectEvent ?? (() => undefined)} busyClipId={busyClipId} onSelectClip={onSelectClip} onActionDone={onActionDone} />
      );
    }
    if (!detail && (detailLoading || detailError)) return <Loading error={detailError} onRetry={onLoadDetail} />;
    if (isEventStep && filtered.length === 0) {
      return (
        <div className="wf-state"><h2>Không có video nào khớp bộ lọc</h2>
          <button type="button" className="wf-btn" onClick={() => onStateFilter('ALL')}>Bỏ bộ lọc</button></div>
      );
    }
    const list = groups.map((g) => {
      const open = isSceneOpen(g.sceneId);
      const locked = eff === 'ATTN' && !g.events.some((e) => attnIds.has(e.eventId));
      const shown = eff === 'ATTN' ? g.events.filter((e) => attnIds.has(e.eventId)) : g.events;
      return (
        <WorkflowSceneBlock key={g.sceneId} group={g} info={sceneInfo.get(g.sceneId)} rows collapsible open={open} locked={locked}
          line={sceneLineOf(g, step, planSteps)} onToggle={() => setSceneOpen((m) => ({ ...m, [g.sceneId]: !open }))}
          tools={step === 'SCRIPT' && detail?.scenes.find((x) => x.id === g.sceneId)
            ? <WorkflowSceneTools planId={planId} scene={detail.scenes.find((x) => x.id === g.sceneId)!} castOptions={refNames} onActionDone={onActionDone} /> : undefined}>
          {shown.map((e) => {
            const st = stateOfEvent(e, step, planSteps);
            const ev = eventsById.get(e.eventId)?.ev ?? null;
            const sb = storyboards ? (storyboards.find((x) => x.eventId === e.eventId)?.storyboard ?? null) : undefined;
            const summary = rowSummaryOf(step, { event: e, ev, alertCount: alertCount.get(`${e.eventId}|${step}`) ?? 0, storyboard: sb, maps, runObjects: status.stepRuns?.[step]?.objects });
            const rOpen = rowOpen[e.eventId] ?? expandAll ?? false;
            return (
              <WorkflowVideoRow key={e.eventId} order={e.eventOrder} step={step} state={st}
                chip={isEventStep ? { text: STATE_META[st].label, tone: STATE_META[st].tone } : { text: badge.text, tone: badge.tone }}
                summary={summary}
                open={rOpen} onToggle={() => setRowOpen((m) => ({ ...m, [e.eventId]: !rOpen }))} onOpenDrawer={() => onOpenEvent(e.eventId, step)}>
                <WorkflowStepContent step={step} eventId={e.eventId} ev={ev} scene={detail?.scenes.find((x) => x.id === e.sceneId) ?? null} detail={detail}
                  storyboards={storyboards} maps={maps} storyboardsError={storyboardsError} mapsError={mapsError} />
              </WorkflowVideoRow>
            );
          })}
        </WorkflowSceneBlock>
      );
    });
    return list;
  };

  const showFilters = isEventStep && !mergedStep && bar.show;
  return (
    <section className="wf-stepsec" aria-labelledby="wf-stepsec-h" data-testid={`wf-stepboard-${step}`}>
      <div className="wf-sth">
        <div className="wf-sth-t">
          <h2 id="wf-stepsec-h">{meta.no}. {meta.name} <StatusIcon text={badge.text} tone={badge.tone} testId="wf-sth-status" />
            <Tooltip title={`${meta.desc}${meta.scope === 'PLAN' ? ' Cấp plan: dùng chung cả tập.' : ''}`}>
              <span className="wf-sth-i" tabIndex={0} role="img" aria-label="Mô tả bước" data-testid="wf-sth-info"><InfoCircleOutlined /></span>
            </Tooltip>
          </h2>
        </div>
        {shownActions.length ? <div className="wf-sth-a">
          {shownActions.map((a, i) => (headActionProminentOf(shownActions, i)
            // Nút nổi bật của đầu mục (bấm được, đứng cuối) giữ chữ + biểu tượng; các nút còn lại là nút biểu tượng kèm Tooltip.
            ? <WfIconButton key={a.key} icon={headActionIconOf(a.key)} label={a.label} className="primary" disabled={sending} onClick={() => onRun(a.call)} testId={`wf-sth-${a.key}`}>{a.label}</WfIconButton>
            : <WfIconButton key={a.key} icon={headActionIconOf(a.key)} label={a.label} why={a.reason} disabled={!a.enabled || sending} onClick={() => onRun(a.call)} testId={`wf-sth-${a.key}`} />
          ))}
        </div> : null}
      </div>
      {/* UI-directing: «tài sản bổ sung» chỉ là MỘT dòng khi đuôi Đạo diễn cần người; không liệt kê từng mục, không ảnh tài sản đã xong. */}
      {step === 'DIRECTING' && onGoStep ? <WorkflowDirectingTailLine status={status} onGoAssets={() => onGoStep('ASSETS')} /> : null}
      <WorkflowQualityNote status={status} step={step} hideWait={qualityWaitRedundantOf(qWait.text, primary)} />
      {disabledWhy ? <p className="wf-sth-why" data-testid="wf-sth-why">{disabledWhy}</p> : null}
      {alerts.map((a, i) => <div key={i} className={`wf-alert ${a.severity}`}>{a.message}</div>)}
      {mergedStep ? <LookMismatchBar detail={detail} onOpenEvent={(id) => onOpenEvent(id, 'VIDEO')} /> : null}
      {splitBanner ? <WorkflowNeedsSplitBanner banner={splitBanner} eventOrders={splitRem.eventOrders} call={rerunCall} sending={sending} onRun={onRun} /> : null}
      {showFilters || isListStep ? (
        <div className="wf-toolbar">
          {showFilters ? (
            <div className="wf-filters" role="group" aria-label="Lọc theo trạng thái">
              {bar.chips.map((c) => (
                <Tooltip key={c.key} title={c.full}>
                  <button type="button" className={`wf-flt ic${c.attn ? ' attn' : ''}${eff === c.key ? ' on' : ''}`} aria-pressed={eff === c.key} aria-label={`${c.short}: ${c.count}`}
                    onClick={() => onStateFilter(c.key)} data-testid={c.attn ? 'wf-flt-attn' : `wf-flt-${c.key}`}>
                    <span className={`wf-si ${c.tone}`}><WfIcon name={c.icon} /></span><span className="lb">{c.short}</span><span className="n">{c.count}</span>
                  </button>
                </Tooltip>
              ))}
            </div>
          ) : <span />}
          {isListStep ? (
            <div className="wf-expand">
              <WfIconButton icon="expand" label="Mở hết" onClick={() => { setExpandAll(true); setRowOpen({}); setSceneOpen(Object.fromEntries(groupsAll.map((g) => [g.sceneId, true]))); }} testId="wf-expand-all" />
              <WfIconButton icon="collapse" label="Thu hết" onClick={() => { setExpandAll(false); setRowOpen({}); }} testId="wf-collapse-all" />
            </div>
          ) : null}
        </div>
      ) : null}
      {isEventStep && !mergedStep && eff === 'ATTN' ? (
        <p className="wf-attn" data-testid="wf-attn-note">Đang xem <b>{filtered.length}</b>/{sorted.length} video cần xử lý (lỗi · thiếu · cũ).{' '}
          <button type="button" className="wf-plink" onClick={() => onStateFilter('ALL')} data-testid="wf-attn-all">Xem tất cả</button></p>
      ) : null}
      {step !== 'ASSETS' ? <WorkflowAssetFlags rows={stepFlagRowsOf(status, step)} events={sorted} step={step} actions={headerActions} primary={primary} sending={sending} onRun={onRun} /> : null}
      {mergedLine ? <p className="wf-attn" data-testid="wf-pv-summary">{mergedLine}</p> : null}
      {body()}
    </section>
  );
};

export default WorkflowStepBoard;
